import { Environment, OrbitControls, useGLTF, useProgress } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import gsap from "gsap";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from 'three';
import Outside from "./env/Outside";
import Ocean from "./env/Pool";
const CameraWithAnimation = ({ glbPath }) => {
  const { scene, cameras, animations } = useGLTF(glbPath);
  const { set, camera: defaultCamera, size, invalidate } = useThree();
  const mixerRef = useRef(null);
  const actionRef = useRef(null); // Reference for animation action
  const { progress } = useProgress();
  const [targetRotation, setTargetRotation] = useState(new THREE.Vector3());
  const smoothRotation = useRef(new THREE.Vector3());

  // Camera and animation setup
  useMemo(() => {
    if (cameras.length > 0 && animations.length > 0) {
      const animatedCamera = cameras[0];
      animatedCamera.zoom = 1.5;
      animatedCamera.updateProjectionMatrix();

      const mixer = new THREE.AnimationMixer(scene);
      mixerRef.current = mixer;
      
      const action = mixer.clipAction(animations[0]);
      actionRef.current = action;
      action.time = 0; 
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;

      action.play(); // Pause at the first frame

      set({ camera: animatedCamera });

      const handleResize = () => {
        animatedCamera.aspect = size.width / size.height;
        animatedCamera.updateProjectionMatrix();
      };

      handleResize();
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        set({ camera: defaultCamera });
        mixer.stopAllAction();
      };
    }
  }, [cameras, animations, scene, set, size, defaultCamera]);

  // Mouse move listener to calculate target rotation
  useEffect(() => {
    const handleMouseMove = (event) => {
      const { innerWidth: width, innerHeight: height } = window;

      // Normalize mouse coordinates to [-0.5, 0.5]
      const mouseX = (event.clientX / width) - 0.5;
      const mouseY = (event.clientY / height) - 0.5;

      // Map normalized mouse coordinates to desired rotation angles
      const rotationX = THREE.MathUtils.lerp(-0.2, 0.2, mouseY); // Tilt up/down
      const rotationY = THREE.MathUtils.lerp(-0.2, 0.2, mouseX); // Rotate left/right

      setTargetRotation(new THREE.Vector3(rotationX, rotationY, 0));
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // Play animation once progress reaches 100%
  useEffect(() => {
    if (progress === 100) actionRef.current.play();
  }, [progress]);

  // Update frame
  useFrame((state, delta) => {
    if (mixerRef.current && actionRef.current) {
      mixerRef.current.update(delta);
      if (actionRef.current.time >= actionRef.current.getClip().duration) {
        actionRef.current.paused = true;
      }
    }

    // Smoothly interpolate the camera's rotation
    if (cameras.length > 0) {
      const animatedCamera = cameras[0];
      smoothRotation.current.lerp(targetRotation, 0.1); // Smooth transition with lerp
      animatedCamera.position.x = smoothRotation.current.x/5;
      animatedCamera.rotation.y = smoothRotation.current.y/8;
      animatedCamera.rotation.z = smoothRotation.current.z;
    }

    invalidate(); // Trigger render
  });

  return <primitive object={scene} />;
};
const Loader = ({ onLoaderFinish }) => {
  const { progress } = useProgress();
  const loaderRef = useRef();
  const topPartRef = useRef();
  const bottomPartRef = useRef();
  const progressRef = useRef(); // For showing progress text

  // Function to trigger the animation when the progress is complete
  const triggerLoaderAnimation = () => {
    const tl = gsap.timeline({
      onComplete: () => {
        onLoaderFinish();
      }
    });

    tl.to(progressRef.current, { opacity: 0, duration: 1.2, ease: "power2.inOut" })
      .to(topPartRef.current, { height: "15vh", duration: 1.2, ease: "power2.inOut" })
      .to(bottomPartRef.current, { height: "15vh", duration: 1.2, ease: "power2.inOut" }, 1.2)
      .to(topPartRef.current, { height: "0vh", duration: 1.2, ease: "power2.inOut" })
      .to(bottomPartRef.current, { height: "0vh", duration: 1.2, ease: "power2.inOut" }, 2.4);
  };

  // Check if the progress is complete and trigger animation
  useMemo(() => {
    if (progress === 100) {
      triggerLoaderAnimation(); // Trigger the animation
    }
  }, [progress]);

  return (
    <div ref={loaderRef} style={styles.loaderContainer}>
      {/* Top and bottom parts of the loader */}
      <div ref={topPartRef} style={styles.topPart} />
      <div ref={bottomPartRef} style={styles.bottomPart} />

      {/* Progress display and disclaimer */}
      <div ref={progressRef} style={styles.progressContainer}>
        <div style={styles.progressText}>{Math.round(progress)}%</div>
        <div style={styles.disclaimerText}>
        </div>
      </div>
    </div>
  );
};

const styles = {
  loaderContainer: {
    width: "100vw",
    height: "100vh",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 22332222,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  topPart: {
    width: "100%",
    height: "100vh", // Start with full height covering the entire screen
    backgroundColor: "#000",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1, // Ensure it's stacked on top
  },
  bottomPart: {
    width: "100%",
    height: "100vh", // Start with full height covering the entire screen
    backgroundColor: "#000",
    position: "absolute",
    bottom: 0,
    left: 0,
    zIndex: 1, // Ensure it's stacked on top
  },
  progressContainer: {
    color: '#fff',
    textAlign: 'center',
    zIndex: 22332223,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6.5rem', // Add space between the progress, disclaimer, and button
  },
  progressText: {
    fontSize: 'clamp(3rem, 8vw, 8rem)', // Responsive font size that scales
  },
  disclaimerText: {
    fontSize: 'clamp(1rem, 3vw, 1.5rem)', // Increased font size for better readability
    fontFamily: 'Rajdhani',
    fontWeight: 300, // Thinner text
    maxWidth: '700px', // Constrain the max width to a smaller size
    width: '100%', // Ensure the width fills the available space, constrained by maxWidth
    lineHeight: '1.4', // Improve readability with more line spacing
  }
};
function Controls() {
  const controlsRef = useRef();

  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping={true}
    // target={[-0.010079476702871463, 0.9319534948772252, 
    //   0.013674165077735131]} // Set the target
    />
  );
}

export default function Experience() {
  const [isSceneLoaded, setIsSceneLoaded] = useState(false);

  const handleLoaderFinish = () => {
    setIsSceneLoaded(true);
  };

  return (
    <>
      {!isSceneLoaded && <Loader onLoaderFinish={handleLoaderFinish} />}
      <Suspense fallback={null}>
        <Canvas
          dpr={[1, 2]}
          frameloop="demand"
          gl={{ antialias: true, depth:true}}
        >
          <CameraWithAnimation glbPath="/Camera.glb" />   {/* <Controls></Controls> */}
          <Environment files="/the_sky_is_on_fire_1k (2).hdr" background backgroundRotation={[0,60,0]} ></Environment>
          <Outside />
          <Ocean />
          {/* <Controls></Controls> */}
        </Canvas>
      </Suspense>
    </>
  );
}
